import type { TelaDeEntrada, TemaDaArea } from './api.js';

/**
 * ── A SEMENTE: A CARA DA ÁREA JÁ NO DOCUMENTO (`CE3`) ────────────────────────
 *
 * O irmão de `endereco.ts`, e o argumento é o mesmo — lá está escrito assim:
 *
 * > **Por que a área vem do HTML, e não de um `fetch`** — porque o roteador é
 * > montado no primeiro render. Buscá-la por rede faria a aplicação nascer sem
 * > saber para onde mandar `/` — uma tela em branco piscando em todo
 * > carregamento.
 *
 * A COR e a ESTRUTURA da área são a segunda coisa dessa lista, e piscavam pelo
 * mesmo motivo: `useCaraDaEntrada` buscava-as num `useEffect`, que corre depois
 * do primeiro desenho. Até a resposta voltar, a tela era `#0b0b0f` de fundo,
 * `#22c55e` de destaque e estrutura `sobria` — os valores de fábrica —, e quem
 * tinha configurado laranja e lateral via o piscar em todo carregamento.
 *
 * Quem semeia é `apps/api/src/lib/semente-do-aluno.ts`, e o cabeçalho de lá
 * tem a medição.
 *
 * ## Por que lida UMA vez, no carregamento do módulo
 *
 * Pelo motivo que `AREA_DO_HOST` já escreveu: o documento não muda no meio de
 * uma sessão, e reler a cada render convidaria alguém a mudá-la em teste e a ver
 * a aplicação divergir do que já está desenhado. A partir daqui a verdade é o
 * que a rede trouxer — a semente é só o PRIMEIRO desenho, nunca o último.
 *
 * ## O que ela NÃO é
 *
 * **Não é autorização, e não é cache de dado.** O que atravessa daqui é o que
 * `GET /aluno/api/entrada/:slug` já devolve a qualquer um sem sessão: nome,
 * cores, foto, logo. Nada do catálogo, nada de contagem, nada de quem estuda
 * ali. Uma semente ausente ou de outro slug é tratada como ausente, e a tela
 * busca como buscava antes — o produto tem de funcionar com o documento cru,
 * porque em desenvolvimento (Vite) ele É cru.
 */

declare global {
  interface Window {
    __VESTIGO_SEMENTE__?: {
      slug?: unknown;
      entrada?: unknown;
      cadastro?: unknown;
    };
  }
}

/** A cara já resolvida, ou `null`. Espelha `SementeDoAluno` no servidor. */
interface Semente {
  slug: string;
  entrada: TelaDeEntrada | null;
  cadastro: TelaDeEntrada | null;
}

/**
 * Uma cara só é cara se tiver o bloco que a tela LÊ.
 *
 * A guarda existe porque o que chega aqui é um objeto de um `<script>` do
 * documento, e não uma resposta com contrato conferido. Um `entrada` ausente
 * faria `cara.entrada.estrutura` lançar dentro do primeiro render — o pior sítio
 * possível, porque cai antes de a cerca de erro existir e a tela fica preta.
 * Recusar devolve a aplicação ao caminho de sempre, que funciona.
 *
 * ## Por que ela é EXPORTADA desde 23/09/2026
 *
 * Porque a semente era a única entrada guardada, e não é a única entrada. A
 * mesma cara chega por `GET /entrada/:slug`, e ali ela era posta no estado sem
 * conferência nenhuma: `useCaraDaEntrada` fazia `.then((c) => setBuscada(c))`.
 * O documento é injetado pelo nosso servidor e a resposta também — mas a régua
 * não é sobre confiar na origem, é sobre o que acontece quando o corpo muda de
 * forma. Trinta linhas de `cara?.entrada.X` nas três telas de entrada lêem esse
 * objeto com o `?` no sítio errado: ele guarda `cara` e não `cara.entrada`.
 *
 * Medido a 23/09/2026 pela cerca `depois-de-entrar-a-area-desenha-sem-recarregar`:
 * com um corpo sem `entrada`, `MolduraDaEntrada` lança
 * `Cannot read properties of undefined (reading 'estrutura')` e a tela de entrar
 * inteira desaparece. Guardar aqui conserta as trinta de uma vez — e é o
 * contrário de pôr `?` em cada uma, que deixaria a trigésima primeira por
 * guardar.
 */
export function caraValida(valor: unknown): valor is TelaDeEntrada {
  return (
    typeof valor === 'object' &&
    valor !== null &&
    typeof (valor as { entrada?: unknown }).entrada === 'object' &&
    (valor as { entrada?: unknown }).entrada !== null
  );
}

const SEMENTE: Semente | null = (() => {
  const cru = typeof window !== 'undefined' ? window.__VESTIGO_SEMENTE__ : undefined;
  if (!cru || typeof cru.slug !== 'string' || cru.slug === '') return null;
  return {
    slug: cru.slug,
    entrada: caraValida(cru.entrada) ? cru.entrada : null,
    cadastro: caraValida(cru.cadastro) ? cru.cadastro : null,
  };
})();

/**
 * A cara semeada para ESTE slug, ou `null`.
 *
 * ## Por que o slug é conferido, e não suposto
 *
 * Porque a SPA navega sem recarregar. Quem abre `/a/escola-a/entrar` e clica
 * num link para `/a/escola-b/entrar` continua com o documento — e portanto com a
 * semente — da primeira. Servir a cara de A na tela de B é o defeito que
 * `lib/tema.ts` já recusa noutra camada: *«o verde da área A permaneceria por
 * baixo da área B»*. Aqui o conserto é a igualdade de slug, e o custo de errar
 * é mostrar a marca de um cliente na porta de outro.
 */
export function caraSemeada(
  slug: string,
  familia: 'entrada' | 'cadastro' = 'entrada',
): TelaDeEntrada | null {
  if (!SEMENTE || SEMENTE.slug !== slug) return null;
  return SEMENTE[familia];
}

/**
 * O tema semeado — para a VITRINE, que não tem cara mas tem as mesmas cores.
 *
 * `cfg.tema` é literalmente o mesmo objeto que `GET /a/:slug` devolve em `tema`
 * (ver `routes/aluno.ts`), e é por isso que a semente da entrada serve as duas
 * telas. O que a vitrine ganha com ele é o quadro de ESPERA: sem isto, o
 * `<Moldura>Carregando…</Moldura>` desenhava o preto de fábrica antes de a
 * vitrine chegar, e trocava de cor à vista de quem esperava.
 */
export function temaSemeado(slug: string): TemaDaArea | undefined {
  const cara = caraSemeada(slug);
  return cara?.tema;
}
